/**
 * varAutocomplete.ts — pure logic for inline ${ autocomplete.
 * No DOM, no React. Fully unit-testable.
 */
import { VarDef } from "./emailVars";

/** Pattern: match "${" followed by zero or more word chars at the END of a string. */
const TRIGGER_RE = /\$\{(\w*)$/;

export interface MatchResult {
	/** The partial text matched (after "${"), e.g. "stat" from "${stat" */
	partial: string;
	/** Offset in the text node where the "${partial" sequence starts */
	tokenStart: number;
	/** Offset where the match ends (= caret position) */
	tokenEnd: number;
}

/**
 * matchTrigger — given the text before the caret, determines if the
 * "${partial" trigger is active.
 *
 * @param textBeforeCaret  Substring of a text node from index 0 up to caret offset
 * @returns MatchResult or null if no trigger is active
 */
export function matchTrigger(textBeforeCaret: string): MatchResult | null {
	const m = TRIGGER_RE.exec(textBeforeCaret);
	if (!m) return null;
	return {
		partial: m[1],
		tokenStart: m.index,
		tokenEnd: textBeforeCaret.length,
	};
}

/**
 * filterCatalog — filter var catalog by partial (case-insensitive match on name or label).
 */
export function filterCatalog(catalog: VarDef[], partial: string): VarDef[] {
	if (!partial) return catalog;
	const q = partial.toLowerCase();
	return catalog.filter((v) => v.n.toLowerCase().includes(q) || v.d.toLowerCase().includes(q));
}

/**
 * getCaretRect — given a Range collapsed at the caret, returns its bounding rect.
 * Falls back to the host element rect when the range produces a zero-size rect
 * (can happen right after a chip node).
 */
export function getCaretRect(range: Range, fallbackEl: Element): DOMRect {
	let rect: DOMRect | undefined;
	try {
		rect = range.getBoundingClientRect();
	} catch (_) {
		// ignore
	}
	if (!rect || (!rect.height && !rect.width)) {
		rect = fallbackEl.getBoundingClientRect();
	}
	return rect;
}
